import { reactive } from 'vue';
import { createRouter as createVueRouter, createWebHistory, createMemoryHistory } from 'vue-router';
import RouteHandler from './RouteHandler.vue';
import qs from 'query-string';
import { STEP_ID } from './components/BookingApp/stores/bookingAppStore';

// support languages in the URL prefix
// e.g. /da-DK/path, or /en/path, or /path
export const routePatterns = [
  '/:lang([a-z]{2}-[A-Z]{2})/:sitecoreRoute(.*)*',
  '/:lang([a-z]{2})/:sitecoreRoute(.*)*',
  '/:sitecoreRoute(.*)*',
];

export const routeChangeStatus = reactive({
  currentInternalRoute: null,
  previousInternalRoute: null,
  changeIndex: 0,
});

const cleanBookingPath = (url) => {
  const bookingStepsIds = Object.values(STEP_ID);
  const [path, ...rest] = url.split(/[?#]/);
  const segments = path.split('/').filter(Boolean);

  if (segments.length > 0 && bookingStepsIds.includes(segments[segments.length - 1])) {
    segments.pop();
  }
  let cleaned = '/' + segments.join('/');
  if (rest.length) {
    cleaned += url.slice(path.length);
  }
  return cleaned;
};

export function createRouter(isSSR) {
  // create an instance of vue-router and configure routes to use the RouteHandler component
  const router = createVueRouter({
    history: isSSR ? createMemoryHistory() : createWebHistory(),
    routes: routePatterns.map((routePattern) => {
      return {
        path: routePattern,
        component: RouteHandler,
        props: (route) => ({
          route,
        }),
      };
    }),
    parseQuery: (query) => qs.parse(query),
    stringifyQuery: (query) => qs.stringify(query, { sort: false }).replaceAll('%20', '+'),
    scrollBehavior(to, from, savedPosition) {
      if (to.hash) {
        return {
          el: to.hash,
          behavior: 'smooth',
        };
      }

      if (to.path === from.path && (to.query.type || to.query.section)) {
        return;
      }

      if (savedPosition) {
        return savedPosition;
      }

      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ left: 0, top: 0 });
        }, 100);
      });
    },
  });

  router.beforeEach((to, from, next) => {
    if (to?.fullPath && to.fullPath !== routeChangeStatus.currentInternalRoute) {
      routeChangeStatus.currentInternalRoute = cleanBookingPath(to.fullPath);
    }

    if (typeof document !== 'undefined' && routeChangeStatus.changeIndex !== 0) {
      routeChangeStatus.previousInternalRoute = cleanBookingPath(from.fullPath);
    }

    routeChangeStatus.changeIndex += 1;

    if (typeof document !== 'undefined' && document?.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    next();
  });

  return router;
}
